/**
 * PB-Detail · Aufgaben & Notizen — voll funktionaler CRUD
 *
 * Linke Spalte: Tasks — anlegen, editieren (Titel/Beschreibung/Fälligkeit),
 *   Status-Toggle, löschen.
 * Rechte Spalte: Notices — anlegen, editieren, löschen.
 *
 * Batch-13 Paket E — Familienkonto-Zentrierung: hat der User ein
 * Familienkonto (`user.family_account_id`, nur PB — BK-User haben keines),
 * läuft das Tab über die Konto-weiten v2-Endpoints
 * (familyAccountTasks/familyAccountNotices — /v2/admin/family-accounts/{id}/
 * tasks|notices), zeigt Einträge ALLER Mitglieder mit Personen-Chip und
 * bietet einen Personen-Filter. Ohne Konto (BK, oder PB ohne
 * family_account_id) bleibt der bisherige personenbezogene v1-Weg
 * unverändert (v1Tasks/v1Notices, /v1/admin/user/{id}/tasks|notices).
 *
 * API:  v1Tasks.create / update / delete   (POST/PUT/DELETE /v1/admin/task/{id})
 *       v1Notices.create / update / delete  (POST/PUT/DELETE /v1/admin/notice/{id})
 *       familyAccountTasks/familyAccountNotices (Konto-Modus, additiv)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../../components/ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../../lib/theme';
import { useIsMobile } from '../../../../lib/useBreakpoint';
import { v1Users, v1Tasks, v1Notices, type V1User } from '../../../../lib/v1';
import { familyAccountTasks, familyAccountNotices } from '../../../../lib/familyAccountData';
import { v2FamilyAccounts } from '../../../../lib/familyAccounts';
import { useActiveAdmins } from '../../PbDashboard/helpers';
import { fmtDateTime } from './helpers';
import { readNoticeDraft } from './NoticeForm';
import { TaskForm } from './TaskForm';
import { NoticeForm } from './NoticeForm';
import { ConfirmDelete } from './ConfirmDelete';
import { TaskRowCard } from './TaskRowCard';
import { NoticeRowCard } from './NoticeRowCard';
import { sortNotices } from './types';
import { isDeceased, useCanPinNotice } from '../../../../lib/noticePin';
import type { TaskRow, NoticeRow, PersonOption } from './types';

interface Props {
  user: V1User | null;
  dark: boolean;
  /**
   * When set, only one column is rendered at full width:
   *   'tasks'   → Aufgaben-Spalte (no Notizen column)
   *   'notices' → Notizen-Spalte (no Aufgaben column)
   * Omit (or undefined) to render both columns (default, used by the real tab).
   */
  only?: 'tasks' | 'notices';
  /**
   * Beschriftung der Notizen-Spalte: 'notizen' (Default, BK-Seite) oder
   * 'infos' (PB-Seite seit 23.10.2026, „Aufgaben und Infos"). Nur Labels,
   * die gespeicherten Daten (notices) bleiben unverändert.
   */
  noticeTerm?: 'notizen' | 'infos';
}

export function AufgabenNotizenTab({ user, dark, only, noticeTerm = 'notizen' }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const infos = noticeTerm === 'infos';
  const isMobile = useIsMobile();
  // Batch-12: Admin-Liste für die Bearbeiter-Auswahl (wie im Kundendashboard).
  const admins = useActiveAdmins();

  // Batch-13 Paket E — Konto-Modus: nur PB mit family_account_id (BK-User
  // haben keines). null → unverändertes personenbezogenes v1-Verhalten.
  const accountId: number | null = (user as { family_account_id?: number | null } | null)?.family_account_id ?? null;

  // Mitglieder des Kontos — für die Personen-Auswahl in den Formularen und
  // den Personen-Filter. Nur geladen, wenn ein Konto vorliegt.
  const [members, setMembers] = useState<PersonOption[]>([]);
  useEffect(() => {
    if (accountId == null) { setMembers([]); return; }
    let alive = true;
    v2FamilyAccounts.get(accountId)
      .then(acc => {
        if (!alive) return;
        const opts: PersonOption[] = (acc.members ?? [])
          .filter(m => m.user_id != null)
          .map(m => ({
            user_id: m.user_id as number,
            name: (m.fullname || [m.vorname, m.nachname].filter(Boolean).join(' ')).trim() || `#${m.user_id}`,
          }));
        setMembers(opts);
      })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [accountId]);

  // Personen-Filter (Konto-Modus): 'all' oder eine user_id. Rein clientseitig
  // auf die bereits geladene Seite angewandt — kein zusätzlicher Request.
  const [personFilter, setPersonFilter] = useState<'all' | number>('all');

  // ── Tasks ──────────────────────────────────────────────────────
  const [tasksRaw, setTasksRaw] = useState<TaskRow[]>([]);
  const [loadingT, setLoadingT] = useState(true);
  const [errorT, setErrorT] = useState<string | null>(null);
  const [showFinished, setShowFinished] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState<'new' | number | null>(null); // 'new' | taskId | null
  const [deleteTaskId, setDeleteTaskId] = useState<number | null>(null);

  const reloadTasks = useCallback(() => {
    if (!user?.id) { setLoadingT(false); return; }
    let alive = true;
    setLoadingT(true);
    setErrorT(null);

    if (accountId != null) {
      familyAccountTasks.list(accountId, { per_page: 50 })
        .then(p => {
          if (!alive) return;
          // showFinishedTasks-Filter existiert auf dem Konto-Endpoint nicht
          // (keine Zielgruppe dafür) — clientseitig wie v1 gefiltert.
          const rows = showFinished ? p.data : p.data.filter(row => row.status !== 1);
          setTasksRaw(rows as unknown as TaskRow[]);
        })
        .catch(e => { if (alive) setErrorT(e instanceof Error ? e.message : tr('pflege.tasks.error_load')); })
        .finally(() => { if (alive) setLoadingT(false); });
      return () => { alive = false; };
    }

    v1Users.tasks(user.id, {
      perPage: 50,
      filters: { showFinishedTasks: showFinished ? 'true' : 'false' },
    })
      .then(p => { if (alive) setTasksRaw(p.data as unknown as TaskRow[]); })
      .catch(e => { if (alive) setErrorT(e instanceof Error ? e.message : tr('pflege.tasks.error_load')); })
      .finally(() => { if (alive) setLoadingT(false); });
    return () => { alive = false; };
  }, [user?.id, accountId, showFinished, tr]);

  useEffect(() => { reloadTasks(); }, [reloadTasks]);

  // Personen-Filter (nur relevant im Konto-Modus) auf die geladene Seite
  // anwenden — rein clientseitig, kein zusätzlicher Request.
  const tasks = useMemo(() => {
    if (accountId == null || personFilter === 'all') return tasksRaw;
    return tasksRaw.filter(row => row.user_id === personFilter);
  }, [tasksRaw, accountId, personFilter]);

  const handleTaskSaved = useCallback((saved: TaskRow) => {
    setTasksRaw(prev => {
      const idx = prev.findIndex(t => t.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    setShowTaskForm(null);
  }, []);

  const handleTaskToggle = useCallback(async (task: TaskRow, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = task.status === 1 ? 0 : 1;
    setTasksRaw(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    try {
      if (accountId != null) {
        await familyAccountTasks.update(task.id, { status: newStatus });
      } else {
        await v1Tasks.update(task.id, {
          titel: task.titel ?? '(Kein Titel)',
          beschreibung: task.beschreibung ?? '',
          status: newStatus,
        });
      }
    } catch {
      setTasksRaw(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
    }
  }, [accountId]);

  const handleTaskDelete = useCallback(async (id: number) => {
    try {
      if (accountId != null) {
        await familyAccountTasks.delete(id);
      } else {
        await v1Tasks.delete(id);
      }
      setTasksRaw(prev => prev.filter(t => t.id !== id));
    } catch {/* ignore */}
    setDeleteTaskId(null);
  }, [accountId]);

  // ── Notices ─────────────────────────────────────────────────────
  const [noticesRaw, setNoticesRaw] = useState<NoticeRow[]>([]);
  const [loadingN, setLoadingN] = useState(true);
  const [errorN, setErrorN] = useState<string | null>(null);

  const reloadNotices = useCallback(() => {
    // Nur-Aufgaben-Ansicht (Drawer, Dashboard): Notizen werden nicht gezeigt, also nicht laden.
    if (!user?.id || only === 'tasks') { setLoadingN(false); return; }
    let alive = true;
    setLoadingN(true);
    setErrorN(null);

    if (accountId != null) {
      familyAccountNotices.list(accountId, { per_page: 50 })
        .then(p => { if (alive) setNoticesRaw(p.data); })
        .catch(e => { if (alive) setErrorN(e instanceof Error ? e.message : tr('pflege.tasks.error_load')); })
        .finally(() => { if (alive) setLoadingN(false); });
      return () => { alive = false; };
    }

    v1Notices.listForUser(user.id)
      .then(list => { if (alive) setNoticesRaw(list as NoticeRow[]); })
      .catch(e => { if (alive) setErrorN(e instanceof Error ? e.message : tr('pflege.tasks.error_load')); })
      .finally(() => { if (alive) setLoadingN(false); });
    return () => { alive = false; };
  }, [user?.id, accountId, only, tr]);

  // Personen-Filter auf die geladene Seite anwenden, dann sortieren:
  // gepinnt zuerst, danach neueste zuerst (created_at desc). Der v2-Endpoint
  // liefert bereits so sortiert; v1 nicht — daher clientseitig immer anwenden.
  const notices = useMemo(() => {
    const filtered = (accountId == null || personFilter === 'all')
      ? noticesRaw
      : noticesRaw.filter(row => row.user_id === personFilter);
    return sortNotices(filtered);
  }, [noticesRaw, accountId, personFilter]);

  useEffect(() => { reloadNotices(); }, [reloadNotices]);

  const [showNoticeForm, setShowNoticeForm] = useState<'new' | number | null>(null);
  const [deleteNoticeId, setDeleteNoticeId] = useState<number | null>(null);

  // Reload-Wiederherstellung: liegt ein zwischengespeicherter Notiz-Entwurf vor,
  // das Anlegen-Formular automatisch öffnen, damit der Text wieder sichtbar wird.
  useEffect(() => {
    if (user?.id && readNoticeDraft(user.id)) setShowNoticeForm('new');
  }, [user?.id]);

  const handleNoticeSaved = useCallback((saved: NoticeRow) => {
    // Optimistic update — show immediately, then sync from server.
    setNoticesRaw(prev => {
      const idx = prev.findIndex(n => n.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    setShowNoticeForm(null);
    // Server reload to get accurate pinned-order and updated_at.
    reloadNotices();
  }, [reloadNotices]);

  // Anpinnen / Pin entfernen (wie CRM 1.0). Optimistisch umschalten, dann neu laden.
  const canPin = useCanPinNotice(isDeceased(user));
  const handleNoticePin = useCallback(async (id: number) => {
    setNoticesRaw(prev => prev.map(n => (n.id === id ? { ...n, pinned: n.pinned ? 0 : 1 } : n)));
    try {
      if (accountId != null) {
        await familyAccountNotices.pin(id);
      } else {
        await v1Notices.pin(id);
      }
    } catch {/* Reload stellt den Serverstand wieder her */}
    reloadNotices();
  }, [reloadNotices, accountId]);

  const handleNoticeDelete = useCallback(async (id: number) => {
    try {
      if (accountId != null) {
        await familyAccountNotices.delete(id);
      } else {
        await v1Notices.delete(id);
      }
      setNoticesRaw(prev => prev.filter(n => n.id !== id));
      // Reload from server to sync pinned-order and any server-side changes.
      reloadNotices();
    } catch {/* ignore */}
    setDeleteNoticeId(null);
  }, [reloadNotices, accountId]);

  // Determine which columns to render based on `only` prop.
  const showTasks   = only !== 'notices';
  const showNotices = only !== 'tasks';

  return (
    <div style={{
      // Aufgaben- und Notizen-Karte stehen untereinander (eine Spalte),
      // damit beide die volle Breite nutzen.
      display: 'grid', gap: isMobile ? 12 : 16,
      gridTemplateColumns: '1fr',
    }}>

      {/* Batch-13 Paket E — Personen-Filter (nur im Konto-Modus, wenn das
          Konto mehr als nur die geöffnete Person hat). Wirkt clientseitig
          auf beide Spalten gleichzeitig. */}
      {accountId != null && members.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text }}>
          {tr('pflege.tasks.person_filter_label')}
          <select
            aria-label={tr('pflege.tasks.person_filter_label')}
            value={personFilter}
            onChange={e => setPersonFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ fontSize: 12.5, padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, color: t.text }}
          >
            <option value="all">{tr('pflege.tasks.person_filter_all')}</option>
            {members.map(m => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </select>
        </label>
      )}

      {/* ── Tasks ── */}
      {showTasks && <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>
            {tr('pflege.tasks.count', { count: tasks.length })}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: t.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={showFinished} onChange={e => setShowFinished(e.target.checked)} style={{ cursor: 'pointer' }} />
              {tr('pflege.tasks.show_done')}
            </label>
            <button
              onClick={() => setShowTaskForm('new')}
              style={{ fontSize: 12, fontWeight: 600, background: BRAND_LIGHT, color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}
            >+ {tr('pflege.tasks.add')}</button>
          </div>
        </div>

        {/* new task form */}
        {showTaskForm === 'new' && user?.id && (
          <TaskForm
            dark={dark}
            userId={user.id}
            admins={admins}
            accountId={accountId}
            members={members}
            onSaved={handleTaskSaved}
            onCancel={() => setShowTaskForm(null)}
          />
        )}

        {errorT ? (
          <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{errorT}</div>
        ) : (
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            {!loadingT && tasks.length === 0 && showTaskForm !== 'new' && (
              <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>
                {tr('pflege.tasks.empty')}
              </div>
            )}
            {tasks.map(task => (
              <React.Fragment key={task.id}>
                {showTaskForm === task.id ? (
                  <TaskForm
                    dark={dark}
                    userId={user?.id ?? 0}
                    initial={task}
                    admins={admins}
                    accountId={accountId}
                    members={members}
                    onSaved={handleTaskSaved}
                    onCancel={() => setShowTaskForm(null)}
                  />
                ) : deleteTaskId === task.id ? (
                  <ConfirmDelete
                    dark={dark}
                    label={task.titel ?? tr('pflege.tasks.no_title')}
                    onConfirm={() => handleTaskDelete(task.id)}
                    onCancel={() => setDeleteTaskId(null)}
                  />
                ) : (
                  <TaskRowCard
                    task={task}
                    dark={dark}
                    admins={admins}
                    onToggle={(e) => handleTaskToggle(task, e)}
                    onEdit={() => setShowTaskForm(task.id)}
                    onDelete={() => setDeleteTaskId(task.id)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </Card>}

      {/* ── Notices ── */}
      {showNotices && <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>
            {tr(infos ? 'pflege.tasks.infos_count' : 'pflege.tasks.notes_count', { count: notices.length })}
          </h3>
          <button
            onClick={() => setShowNoticeForm('new')}
            style={{ fontSize: 12, fontWeight: 600, background: BRAND_LIGHT, color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}
          >+ {tr(infos ? 'pflege.tasks.infos_add' : 'pflege.tasks.notice_add')}</button>
        </div>

        {/* new notice form */}
        {showNoticeForm === 'new' && user?.id && (
          <NoticeForm
            dark={dark}
            userId={user.id}
            accountId={accountId}
            members={members}
            onSaved={handleNoticeSaved}
            onCancel={() => setShowNoticeForm(null)}
          />
        )}

        {errorN && (
          <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{errorN}</div>
        )}

        <div style={{ maxHeight: 560, overflowY: 'auto' }}>
          {!loadingN && notices.length === 0 && showNoticeForm !== 'new' && (
            <div style={{ padding: 24, color: t.muted, fontSize: 13, fontStyle: 'italic' }}>
              {tr(infos ? 'pflege.tasks.infos_empty' : 'pflege.tasks.notes_empty')}
            </div>
          )}
          {notices.map(n => (
            <React.Fragment key={n.id}>
              {showNoticeForm === n.id ? (
                <NoticeForm
                  dark={dark}
                  userId={user?.id ?? 0}
                  initial={n}
                  accountId={accountId}
                  members={members}
                  onSaved={handleNoticeSaved}
                  onCancel={() => setShowNoticeForm(null)}
                />
              ) : deleteNoticeId === n.id ? (
                <ConfirmDelete
                  dark={dark}
                  label={tr(infos ? 'pflege.tasks.info_item_label' : 'pflege.tasks.note_item_label', { date: fmtDateTime(n.created_at) })}
                  onConfirm={() => handleNoticeDelete(n.id)}
                  onCancel={() => setDeleteNoticeId(null)}
                />
              ) : (
                <NoticeRowCard
                  notice={n}
                  dark={dark}
                  onEdit={() => setShowNoticeForm(n.id)}
                  onDelete={() => setDeleteNoticeId(n.id)}
                  onTogglePin={canPin(n) ? () => handleNoticePin(n.id) : undefined}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </Card>}
    </div>
  );
}
